import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { of, Subject, Subscription, timer } from 'rxjs';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { BaseServices } from '../../kuba.services';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { MoveBusinessServices } from '../services/move-business.service';
import { TableLazyLoadEvent } from 'primeng/table';
import {
  MoveBusinessCandidate,
  MoveBusinessOwner,
  MoveBusinessRequest,
  MoveBusinessResponse,
  MoveBusinessUser
} from '../models/move-business';

@Component({
  selector: 'move-business',
  templateUrl: 'move-business.component.html',
  styleUrls: ['move-business.component.scss'],
  standalone: false
})
export class MoveBusinessComponent implements OnInit, OnDestroy {
  private readonly api = inject(MoveBusinessServices);
  private readonly translate = inject(TranslateService);
  private readonly notifications = inject(NotificationService);
  private readonly destroy$ = new Subject<void>();
  private destinationLoad?: Subscription;
  private userLoad?: Subscription;
  private businessLoad = new Subscription();
  private sourceOwnerLoad = new Subscription();
  private filterDelay = new Subscription();
  readonly pageSize = 15;
  first = 0;
  totalBusinesses = 0;
  businessesLoading = false;
  sourceOwnersLoading = false;
  readonly sourceApplicationId = Number(BaseServices.ApplicationId);
  targetApplicationId = this.sourceApplicationId;
  step = 0;
  loading = false;
  ownersLoading = false;
  usersLoading = false;
  businesses: MoveBusinessCandidate[] = [];
  selected: MoveBusinessCandidate[] = [];
  sourceOwners: MoveBusinessOwner[] = [];
  owners: MoveBusinessOwner[] = [];
  destination: MoveBusinessOwner | null = null;
  destinationKind: 'admin' | 'portal' | 'application' = 'portal';
  portalUsers: MoveBusinessUser[] = [];
  selectedPortalUsers: number[] = [];
  search = '';
  sourceFilter: MoveBusinessOwner | null = null;
  includeInactive = false;
  preview: MoveBusinessResponse | null = null;
  result: MoveBusinessResponse | null = null;
  acknowledged = false;
  organizationConfirmation = '';
  error = '';
  uncertain = false;
  get platforms() {
    return this.sourceApplicationId === 1 || this.sourceApplicationId === 9
      ? [
          { Id: 1, Name: 'Kuba IK' },
          { Id: 9, Name: 'Agil' },
          { Id: 10, Name: 'Salutis' }
        ]
      : [
          {
            Id: this.sourceApplicationId,
            Name: this.sourcePlatformName
          }
        ];
  }
  get targetPlatformName() {
    return this.platforms.find(p => p.Id === this.targetApplicationId)?.Name;
  }
  get sourcePlatformName() {
    return this.sourceApplicationId === 1
      ? 'Kuba IK'
      : this.sourceApplicationId === 9
        ? 'Agil'
        : this.sourceApplicationId === 10
          ? 'Salutis'
          : this.translate.instant('MOVE_BUSINESS_FLOW.CURRENT_PLATFORM');
  }
  get otherPlatforms() {
    return this.platforms.filter(p => p.Id !== this.sourceApplicationId);
  }
  get availableOwners() {
    return this.owners.filter(owner =>
      this.destinationKind === 'portal'
        ? owner.BusinessPortalId != null
        : owner.BusinessPortalId == null
    );
  }
  get sourceNames() {
    return [
      ...new Set(
        this.selected.map(b => b.OwnerName).filter(name => name != null)
      )
    ].join(', ');
  }
  get selectedNames() {
    return this.selected.map(b => b.CompanyName).join(', ');
  }
  get optionalPortalUsers() {
    const managerId =
      this.destination == null ? null : this.destination.PortalManagerId;
    return this.portalUsers.filter(u => u.Id !== managerId);
  }
  get portalManager() {
    const managerId =
      this.destination == null ? null : this.destination.PortalManagerId;
    return this.portalUsers.find(u => u.Id === managerId);
  }
  get crossPlatform() {
    return this.targetApplicationId !== this.sourceApplicationId;
  }
  get canChangeApplication() {
    return (
      this.selected.length === 1 &&
      this.selected[0].PortalId == null &&
      this.otherPlatforms.length > 0
    );
  }
  get canReview() {
    return (
      !!this.destination &&
      this.selected.length > 0 &&
      this.selected.length <= 20 &&
      (!this.crossPlatform ||
        (this.canChangeApplication &&
          this.destination.BusinessPortalId == null)) &&
      !this.loading &&
      !this.businessesLoading &&
      !this.ownersLoading &&
      !this.usersLoading
    );
  }
  get canCommit() {
    return (
      !!this.preview?.ConfirmationToken &&
      this.acknowledged &&
      !this.loading &&
      (!this.crossPlatform ||
        (this.canChangeApplication &&
          this.destination != null &&
          this.destination.BusinessPortalId == null &&
          this.organizationConfirmation.trim() ===
            (this.selected[0]?.OrganizationNumber ??
              String(this.selected[0]?.Id))))
    );
  }
  get confirmationNumber() {
    return this.selected[0]?.OrganizationNumber ?? String(this.selected[0]?.Id);
  }
  ngOnInit() {
    this.reload();
  }
  reload() {
    this.filterDelay.unsubscribe();
    this.businessLoad.unsubscribe();
    this.sourceOwnerLoad.unsubscribe();
    this.destinationLoad?.unsubscribe();
    this.userLoad?.unsubscribe();
    this.error = '';
    this.uncertain = false;
    this.step = 0;
    this.selected = [];
    this.preview = null;
    this.result = null;
    this.destination = null;
    this.targetApplicationId = this.sourceApplicationId;
    this.first = 0;
    this.search = '';
    this.includeInactive = false;
    this.sourceFilter = null;
    this.sourceOwners = [];
    this.sourceOwnersLoading = true;
    this.sourceOwnerLoad = this.api
      .owners(this.sourceApplicationId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.sourceOwnersLoading = false))
      )
      .subscribe({
        next: owners => {
          this.sourceOwners = owners;
        },
        error: () => {}
      });
    this.loadBusinesses();
  }
  loadBusinesses() {
    this.filterDelay.unsubscribe();
    this.businessLoad.unsubscribe();
    this.businessesLoading = true;
    this.businesses = [];
    this.error = '';
    this.businessLoad = this.api
      .businesses(
        Math.floor(this.first / this.pageSize) + 1,
        this.pageSize,
        this.search,
        this.includeInactive,
        this.sourceFilter
      )
      .pipe(
        switchMap(page => {
          // Another administrator may have moved the last businesses off this page.
          if (this.first > 0 && this.first >= page.TotalCount) {
            this.first = Math.max(
              0,
              Math.floor((page.TotalCount - 1) / this.pageSize) * this.pageSize
            );
            if (page.TotalCount > 0) {
              return this.api.businesses(
                Math.floor(this.first / this.pageSize) + 1,
                this.pageSize,
                this.search,
                this.includeInactive,
                this.sourceFilter
              );
            }
          }
          return of(page);
        }),
        takeUntil(this.destroy$),
        finalize(() => (this.businessesLoading = false))
      )
      .subscribe({
        next: page => {
          this.totalBusinesses = page.TotalCount;
          this.businesses = page.Items;
        },
        error: (error: HttpErrorResponse) => {
          this.totalBusinesses = 0;
          this.showFailure(error, false);
        }
      });
  }
  pageChanged(event: TableLazyLoadEvent) {
    this.first = event.first ?? 0;
    this.loadBusinesses();
  }
  filtersChanged(debounce = false) {
    this.filterDelay.unsubscribe();
    this.businessLoad.unsubscribe();
    this.first = 0;
    this.businesses = [];
    this.businessesLoading = true;
    this.invalidatePreview();
    if (debounce) {
      this.filterDelay = timer(300)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.loadBusinesses());
    } else {
      this.loadBusinesses();
    }
  }
  selectionChanged(selection: MoveBusinessCandidate[]) {
    if (this.businessesLoading || this.loading) return;
    const pageIds = new Set(this.businesses.map(b => b.Id));
    const previous = new Map(this.selected.map(b => [b.Id, b]));
    const onPage = selection.filter(b => pageIds.has(b.Id));
    // Keep the snapshot selected by the user; revisiting a page must not silently refresh its source version.
    this.selected = [
      ...this.selected.filter(b => !pageIds.has(b.Id)),
      ...onPage.map(b => {
        const snapshot = previous.get(b.Id);
        return snapshot != null ? snapshot : b;
      })
    ];
    this.invalidatePreview();
  }
  clearSelection() {
    this.selected = [];
    this.invalidatePreview();
  }
  chooseDestination() {
    this.invalidatePreview();
    this.step = 1;
    this.destinationKind = this.selected.some(b => b.PortalId != null)
      ? 'portal'
      : 'admin';
    this.targetApplicationId = this.sourceApplicationId;
    this.loadOwners();
  }
  changeDestinationKind(kind: 'admin' | 'portal' | 'application') {
    if (this.loading || (kind === 'application' && !this.canChangeApplication))
      return;
    this.destinationKind = kind;
    this.targetApplicationId =
      kind === 'application'
        ? this.otherPlatforms[0].Id
        : this.sourceApplicationId;
    this.loadOwners();
  }
  clearUsers() {
    this.selectedPortalUsers = [];
    this.invalidatePreview();
  }
  warningsFor(businessId: number) {
    return this.preview == null
      ? []
      : this.preview.Warnings.filter(
          warning => warning.BusinessId === businessId
        );
  }
  sourceOwnerName(businessId: number) {
    const business = this.selected.find(b => b.Id === businessId);
    return business == null || business.OwnerName == null
      ? this.translate.instant('MOVE_BUSINESS_FLOW.NONE')
      : business.OwnerName;
  }
  get followUpWarnings() {
    return this.result == null
      ? []
      : this.result.Warnings.filter(warning =>
          [
            'ManualSkipped',
            'PortalManualsRetained',
            'FeatureMissing',
            'FeaturesUnknown',
            'PlatformReferences',
            'HseAdvisorsCleared'
          ].includes(warning.Code)
        );
  }
  loadOwners() {
    this.destinationLoad?.unsubscribe();
    this.userLoad?.unsubscribe();
    this.destination = null;
    this.owners = [];
    this.portalUsers = [];
    this.selectedPortalUsers = [];
    this.usersLoading = false;
    this.invalidatePreview();
    this.ownersLoading = true;
    this.destinationLoad = this.api
      .owners(this.targetApplicationId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.ownersLoading = false))
      )
      .subscribe({
        next: owners => {
          this.owners = owners.filter(
            o =>
              !this.selected.some(
                b =>
                  b.ApplicationId === this.targetApplicationId &&
                  (o.BusinessPortalId
                    ? b.PortalId === o.BusinessPortalId
                    : !b.PortalId && b.OwnerUserId === o.Id)
              )
          );
        },
        error: () => {}
      });
  }
  ownerChanged() {
    this.userLoad?.unsubscribe();
    this.portalUsers = [];
    this.selectedPortalUsers = [];
    this.invalidatePreview();
    if (!this.destination?.BusinessPortalId) return;
    this.usersLoading = true;
    this.userLoad = this.api
      .portalUsers(this.targetApplicationId, this.destination.BusinessPortalId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.usersLoading = false))
      )
      .subscribe({
        next: users => {
          this.portalUsers = users;
        },
        error: () => {
          this.destination = null;
        }
      });
  }
  selectAllUsers() {
    this.selectedPortalUsers = this.portalUsers
      .filter(u => u.Id !== this.destination?.PortalManagerId)
      .map(u => u.Id);
    this.invalidatePreview();
  }
  invalidatePreview() {
    this.preview = null;
    this.acknowledged = false;
    this.organizationConfirmation = '';
    this.error = '';
  }
  private request(): MoveBusinessRequest {
    const destination = this.destination;
    if (!destination) throw new Error('A destination is required');
    return {
      BusinessIds: this.selected.map(b => b.Id),
      TargetApplicationId: this.targetApplicationId,
      TargetUserId: destination.PortalManagerId ?? destination.Id,
      TargetPortalId: destination.BusinessPortalId,
      PortalUserIds: this.selectedPortalUsers,
      SourceVersions: Object.fromEntries(
        this.selected.map(b => [b.Id, b.SourceVersion])
      )
    };
  }
  review() {
    if (!this.canReview) return;
    this.loading = true;
    this.error = '';
    this.uncertain = false;
    this.api
      .preview(this.request())
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: response => {
          if (response.Committed || !response.ConfirmationToken) {
            this.error = this.translate.instant(
              'MOVE_BUSINESS_FLOW.INVALID_RESPONSE'
            );
            return;
          }
          this.preview = response;
          this.acknowledged = false;
          this.step = 2;
        },
        error: (error: HttpErrorResponse) => this.showFailure(error, false)
      });
  }
  commit() {
    const token = this.preview?.ConfirmationToken;
    if (!this.canCommit || !token) return;
    this.loading = true;
    this.error = '';
    this.api
      .commit({ ...this.request(), ConfirmationToken: token })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: response => {
          if (response.Committed !== true || !response.RunId) {
            this.uncertain = true;
            this.error = this.translate.instant(
              'MOVE_BUSINESS_FLOW.INVALID_RESPONSE'
            );
            this.preview = null;
            return;
          }
          this.result = response;
          this.step = 3;
          this.notifications.success('UI_NOTIFICATIONS.SAVED');
        },
        error: (error: HttpErrorResponse) => this.showFailure(error, true)
      });
  }
  private showFailure(error: HttpErrorResponse, committing: boolean) {
    this.uncertain = committing && (error.status === 0 || error.status >= 500);
    this.error =
      typeof error.error?.detail === 'string'
        ? error.error.detail
        : this.translate.instant('MOVE_BUSINESS_FLOW.FAILED');
    this.preview = null;
  }
  ownerLabel(owner: MoveBusinessOwner) {
    return `${this.translate.instant(owner.BusinessPortalId ? 'MOVE_BUSINESS_FLOW.PORTAL' : 'MOVE_BUSINESS_FLOW.ADMIN')} · ${owner.Name}`;
  }
  businessName(id: number) {
    return this.selected.find(b => b.Id === id)?.CompanyName ?? String(id);
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
